import React, { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';
import Card from './ui/Card';
import Button from './ui/Button';
import Select from './ui/Select';
import CategoryPicker from './ui/CategoryPicker';
import Input from './ui/Input';
import { useDialog } from './ui/DialogProvider';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Filter } from 'lucide-react';
import { expenseDistribution } from '../utils/analyticsDistributionUtils';
import { fetchAllAnalyticsTransactions } from '../utils/analyticsTransactions';

const COLORS = ['#12c6a3', '#23b8f2', '#ffd166', '#ff7a59', '#9b8cff', '#5af0b1'];

const Analytics = () => {
  const { notify } = useDialog();
  const [reportType, setReportType] = useState('summary');
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedIncomeSource, setSelectedIncomeSource] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [incomeSources, setIncomeSources] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    fetchReferenceData();
  }, []);

  const fetchReferenceData = async () => {
    try {
      const [categoriesRes, incomeSourcesRes, locationsRes] = await Promise.all([
        apiClient.get('/categories'),
        apiClient.get('/income-sources'),
        apiClient.get('/locations')
      ]);

      setCategories(categoriesRes.data.data || []);
      setIncomeSources(incomeSourcesRes.data.data || []);
      setLocations(locationsRes.data.data || []);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      let categoryIdsParam = null;
      if (selectedCategory) {
        const parentId = parseInt(selectedCategory);
        const children = categories.filter(c => c.parent_id === parentId);
        const ids = [parentId, ...children.map(c => c.id)];
        categoryIdsParam = ids.join(',');
      }

      const params = {
        type: reportType,
        period,
        startDate: startDate || null,
        endDate: endDate || null,
        categoryIds: categoryIdsParam,
        incomeSourceId: selectedIncomeSource || null,
        locationId: selectedLocation || null
      };

      const responseData = await fetchAllAnalyticsTransactions(apiClient, params);
      setReportData(reportType === 'summary' ? responseData : { transactions: responseData });
    } catch (error) {
      console.error('Error generating report:', error);
      await notify('Не удалось сформировать отчёт: ' + (error.response?.data?.message || error.message), { title: 'Ошибка отчёта' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    generateReport();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const filteredCategories = categories;


  const categoryMap = {};
  categories.forEach(cat => {
    categoryMap[cat.id] = cat;
  });

  const incomeSourceMap = {};
  incomeSources.forEach(source => {
    incomeSourceMap[source.id] = source;
  });

  const getCategoryDistributionData = () => {
    if (!reportData?.transactions?.length) return [];
    const selectedCategoryIds = selectedCategory
      ? [Number(selectedCategory), ...categories
        .filter((category) => String(category.parent_id) === String(selectedCategory))
        .map((category) => category.id)]
      : [];
    return expenseDistribution(reportData.transactions, selectedCategoryIds, categoryMap);
  };

  const getIncomeSourceDistributionData = () => {
    if (!reportData?.transactions?.length) return [];

    const incomeTransactions = reportData.transactions.filter(t => t.type === 'income');

    const groupedBySource = incomeTransactions.reduce((acc, transaction) => {
      const sourceName = transaction.income_source_name ||
        (transaction.income_source_id && incomeSourceMap[transaction.income_source_id]?.name) ||
        'Без источника';
      if (!acc[sourceName]) {
        acc[sourceName] = { name: sourceName, value: 0 };
      }
      acc[sourceName].value += Math.abs(parseFloat(transaction.amount));
      return acc;
    }, {});

    return Object.values(groupedBySource);
  };

  const [distributionType, setDistributionType] = useState('expense');

  useEffect(() => {
    if (reportType === 'income') {
      setDistributionType('income');
    } else if (reportType === 'expense') {
      setDistributionType('expense');
    }
  }, [reportType]);

  const distributionData = distributionType === 'income'
    ? getIncomeSourceDistributionData()
    : getCategoryDistributionData();

  return (
    <div className="space-y-10">
      <section>
        <h2 className="text-4xl font-semibold text-ink">Аналитика</h2>
      </section>

      <section className="grid gap-8 lg:grid-cols-[0.7fr_1.3fr]">
        <div className="relative z-10 rounded-xl3 border border-glass-border bg-glass px-6 py-7 backdrop-blur-lg shadow-glass">
          <form onSubmit={handleSubmit} className="space-y-6">
            <Select
              label="Тип отчета"
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
            >
              <option value="summary">Сводный отчет</option>
              <option value="expense">Только расходы</option>
              <option value="income">Только доходы</option>
              <option value="transfer">Только переводы</option>
            </Select>

            <Select
              label="Период"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="day">Сегодня</option>
              <option value="week">Эта неделя</option>
              <option value="month">Этот месяц</option>
              <option value="year">Этот год</option>
              <option value="custom">Произвольный период</option>
            </Select>

            {period === 'custom' && (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Input
                  label="Дата начала"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <Input
                  label="Дата окончания"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            )}

            {(reportType === 'expense' || reportType === 'income') && (
              <CategoryPicker
                label={reportType === 'expense' ? 'Категория расхода' : 'Категория дохода'}
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                categories={filteredCategories}
                allowEmpty
                emptyLabel="Все категории"
                placeholder="Все категории"
              />
            )}

            {reportType === 'income' && (
              <Select
                label="Источник дохода"
                value={selectedIncomeSource}
                onChange={(e) => setSelectedIncomeSource(e.target.value)}
              >
                <option value="">Все источники</option>
                {incomeSources.map(source => (
                  <option key={source.id} value={source.id}>{source.name}</option>
                ))}
              </Select>
            )}

            <Select
              label="Место хранения"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              <option value="">Все места</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>{location.name}</option>
              ))}
            </Select>

            <Button type="submit" disabled={loading} icon={Filter} className="w-full justify-center">
              {loading ? 'Генерация...' : 'Сформировать отчет'}
            </Button>
          </form>
        </div>

        <Card>
          <div className="flex flex-col gap-4 border-b border-line pb-4 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-xl font-semibold text-ink">
              {distributionType === 'income' ? 'Источники дохода' : 'Категории расходов'}
            </h3>
            <div className="flex gap-2 text-xs uppercase tracking-[0.2em] text-muted">
              <button
                onClick={() => setDistributionType('expense')}
                className={`rounded-full border px-3 py-2 ${distributionType === 'expense' ? 'border-accent text-accent' : 'border-line'}`}
              >
                Расходы
              </button>
              <button
                onClick={() => setDistributionType('income')}
                className={`rounded-full border px-3 py-2 ${distributionType === 'income' ? 'border-accent text-accent' : 'border-line'}`}
              >
                Доходы
              </button>
            </div>
          </div>
          <div className="h-80">
            {reportData && distributionData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={distributionData}
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    fill="#8d96a5"
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {distributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(17, 19, 24, 0.9)',
                      borderColor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '0.75rem',
                      color: 'white'
                    }}
                    formatter={(value) => [formatCurrency(value), 'Сумма']}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted">
                Сформируйте отчет, чтобы увидеть распределение.
              </div>
            )}
          </div>
        </Card>
      </section>

      {reportData && reportType === 'summary' && reportData.summary && (
        <div className="rounded-xl3 border border-glass-border bg-glass px-6 py-7 backdrop-blur-lg shadow-glass">
          <div className="grid gap-6 sm:grid-cols-3">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-muted">Доходы</p>
              <p className="text-2xl font-semibold text-accent">{formatCurrency(reportData.summary.totalIncome || 0)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-muted">Расходы</p>
              <p className="text-2xl font-semibold text-ink">{formatCurrency(reportData.summary.totalExpenses || 0)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-muted">Чистый результат</p>
              <p className="text-2xl font-semibold text-ink">
                {formatCurrency(reportData.summary.netAmount || 0)}
              </p>
            </div>
          </div>
        </div>
      )}

      {reportData && (
        <Card title="Детали транзакций">
          <div className="divide-y divide-line">
            {reportData.transactions && reportData.transactions.length > 0 ? (
              reportData.transactions.map(transaction => (
                <div key={transaction.id} className="flex items-center justify-between py-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.24em] text-muted">
                      {new Date(transaction.date).toLocaleDateString()}
                    </p>
                    <p className="text-base text-ink">
                      {transaction.type === 'expense'
                        ? (transaction.category_name || (transaction.category_id && categoryMap[transaction.category_id] ? categoryMap[transaction.category_id]?.name || 'Без категории' : 'Без категории'))
                        : transaction.type === 'income'
                          ? (transaction.income_source_name || (transaction.income_source_id && incomeSourceMap[transaction.income_source_id] ? incomeSourceMap[transaction.income_source_id]?.name || 'Без источника' : 'Без источника'))
                          : 'Перевод'}
                    </p>
                  </div>
                  <div className={`text-lg font-semibold ${transaction.type === 'income' ? 'text-accent' : 'text-ink'}`}>
                    {formatCurrency(transaction.amount)}
                  </div>
                </div>
              ))
            ) : (
              <div className="py-6 text-sm text-muted">Нет транзакций за выбранный период</div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default Analytics;
